import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

const empty = { title:'', description:'', batchRef:'', isImportant:false, isActive:true };

export default function Notices() {
  const [notices, setNotices]         = useState([]);
  const [batches, setBatches]         = useState([]);
  const [modalOpen, setModalOpen]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [form, setForm]               = useState(empty);
  const [editing, setEditing]         = useState(false);

  const fetchAll = async () => { const [n,b]=await Promise.all([API.get('/notices'),API.get('/batches')]); setNotices(n.data); setBatches(b.data); };
  useEffect(()=>{ fetchAll(); },[]);

  const openCreate = () => { setForm(empty); setEditing(false); setModalOpen(true); };
  const openEdit   = (n) => { setForm({...n, batchRef:n.batchRef?._id||''}); setEditing(true); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {...form, batchRef: form.batchRef||null};
    try {
      if (editing) { await API.put(`/notices/${form._id}`, payload); toast.success('Notice updated!'); }
      else         { await API.post('/notices', payload);             toast.success('Notice created!'); }
      setModalOpen(false); fetchAll();
    } catch(err) { toast.error(err.response?.data?.message||'Something went wrong!'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/notices/${deleteId}`); toast.success('Notice deleted!'); setConfirmOpen(false); fetchAll(); }
    catch { toast.error('Could not delete!'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center"><Bell className="text-yellow-600" size={22}/></div>
          <div><h1 className="text-xl font-bold text-slate-800">Notice Board</h1><p className="text-slate-500 text-sm">Manage all notices</p></div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={18}/> New Notice
        </button>
      </div>

      <div className="space-y-3">
        {notices.length===0
          ? <div className="bg-white rounded-xl border border-slate-100 shadow-sm text-center py-12 text-slate-400">No notices found. Create one!</div>
          : notices.map(n=>(
            <div key={n._id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${n.isImportant?'bg-red-500':'bg-green-400'}`}/>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-slate-800 text-sm">{n.title}</h3>
                    {n.isImportant&&<span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full">Urgent</span>}
                    {n.batchRef&&<span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">{n.batchRef.batchName}</span>}
                  </div>
                  <p className="text-slate-500 text-sm">{n.description}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(n.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={()=>openEdit(n)} className="bg-green-50 hover:bg-green-100 text-green-700 p-2 rounded-lg transition-colors"><Pencil size={15}/></button>
                <button onClick={()=>{setDeleteId(n._id);setConfirmOpen(true);}} className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"><Trash2 size={15}/></button>
              </div>
            </div>
          ))}
      </div>

      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editing?'Edit Notice':'New Notice'}>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
            <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" rows={4} required value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Batch (optional)</label>
            <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" value={form.batchRef} onChange={e=>setForm({...form,batchRef:e.target.value})}>
              <option value="">All Batches</option>
              {batches.map(b=><option key={b._id} value={b._id}>{b.batchName}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-red-600" checked={form.isImportant} onChange={e=>setForm({...form,isImportant:e.target.checked})}/>
            <span className="text-sm text-slate-700">Mark as Urgent</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={()=>setModalOpen(false)} className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium text-sm">Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog isOpen={confirmOpen} onConfirm={handleDelete} onCancel={()=>setConfirmOpen(false)}/>
    </div>
  );
}
